import React, { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppDivisionContext from '../../contexts/AppDivisionContext';
import { getDivisionResourceName } from '../../hooks/divisionResource';
import { ProjectListProvider } from '../../contexts/ProjectListContext';
import DivisionError from '../Shared/DivisionError/DivisionError';
import ResourceIndex from '../Shared/ResourceIndex/ResourceIndex';
import RolesContext from '../../contexts/RolesContext';
import TenantFeatureContext from '../../contexts/TenantContext';
import InspectionCoverageReportModal from './InspectionCoverage/InspectionCoverageReportModal';

type Report = {
  id: number;
  name: string;
};

const Reports = () => {
  const roleStore = useContext(RolesContext);
  const appDivisionContext = useContext(AppDivisionContext);
  const tenantFeature = useContext(TenantFeatureContext);

  const canSeeAccountProjectOverview = roleStore.userHasPermission(
    'read',
    'Tenant Project Report',
  );
  const navigation = useHistory();
  const [search, setSearch] = useState('');
  const [showCoverageModal, setShowCoverageModal] = useState(false);

  const handleClick = useCallback(
    (report: Report) => () => {
      if (report && report.name) {
        navigation.push(
          `${appDivisionContext.getPath(`/reports/${report.name}`)}`,
        );
      }

      return null;
    },
    [],
  );

  const renderList = useCallback(
    (_: unknown, reports: Report[]) => {
      const visibleReports = (reports ?? []).filter(
        (e) =>
          e.name.toLowerCase().match(search.toLowerCase()) &&
          tenantFeature.tenantHasFeature(e.name),
      );
      const hasAnyCard =
        canSeeAccountProjectOverview || visibleReports.length > 0;

      return (
        <div className='projects'>
          <div className='flex-row project-list'>
            {!hasAnyCard && <p>No reports found</p>}

            {canSeeAccountProjectOverview && (
              <div className='project'>
                <div
                  className='project-main'
                  onClick={() => setShowCoverageModal(true)}
                >
                  <h3>Account Project Overview</h3>
                  <p>
                    Exports all projects with assigned inspection forms,
                    inspector, manager, submission counts, and last inspection
                    date for your organization
                  </p>
                </div>
              </div>
            )}

            {visibleReports.map((report) => (
              <div className='project' key={report.id}>
                <div className='project-main' onClick={handleClick(report)}>
                  <h3>{report.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    },
    [search, tenantFeature, canSeeAccountProjectOverview, handleClick],
  );

  if (!appDivisionContext.appDivisionId) {
    return <DivisionError resource='Reports' />;
  }

  return (
    <ProjectListProvider>
      <div className='report-search-container'>
        <input
          className='search-control'
          type='search'
          placeholder='Search'
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ResourceIndex
        pageNameForStyling={'Report'}
        resourceName={getDivisionResourceName('Reports')}
        resourceQueryProps={{ divisionId: appDivisionContext.appDivisionId }}
        resourceTableHeaders={[['ID', 'id']]}
        enableRead={roleStore.userHasPermission('all', 'Reports')}
        enableEdit={false}
        enableCallToAction={false}
        enableOverflow={false}
        hideCallToActionInMobile
        enablePagination={false}
        searchInputPlaceholder='Search Reports'
        renderList={renderList}
        enableSearch={false}
      />

      {showCoverageModal && (
        <InspectionCoverageReportModal setShowModal={setShowCoverageModal} />
      )}
    </ProjectListProvider>
  );
};

export default Reports;
